<app-pageheader [Parent]="this">
  @if (CanWrite) {
    <add-button AddRoute="/revenue/transactionbreakdown/feetypes/new/edit" ButtonText="Add Fee Type"></add-button>
    <add-button AddRoute="/revenue/transactionbreakdown/fees/new/edit" ButtonText="Add Fee"></add-button>
    <add-button AddRoute="/revenue/transactionbreakdown/pricingmodes/new/edit" ButtonText="Add Pricing Mode"></add-button>
  }
</app-pageheader>

<div class="columns">
  <div class="column is-7">
    <article class="panel">
      <p class="panel-heading">Fees</p>
      <div class="panel-block is-block">
        <p class="mb-3">Fees are shown to users as breakdown lines, in pipeline order: Base Parking &rarr; Pre-tax Fees &rarr; Taxes &rarr; Post-tax Fees &rarr; Total. By default, quoted amounts are treated as inclusive of fees (reverse-calculated); configure a Pricing Mode below to make a specific entity type exclusive instead (fees added on top of the quoted amount).</p>
        @if (Fees == null) {
          <p>Loading...</p>
        } @else if (Fees.length == 0) {
          <p>No fees configured yet.</p>
        } @else {
          <table class="table is-fullwidth is-striped">
            <thead>
              <tr>
                <th>Display Name</th>
                <th>Value</th>
                <th>Fee Type</th>
                <th>Stage</th>
                <th>Active</th>
                @if (CanWrite) { <th></th> }
              </tr>
            </thead>
            <tbody>
              @for (fee of Fees; track fee.Id) {
                <tr>
                  <td>{{fee.DisplayName}}</td>
                  <td>{{FeeValueLabel(fee)}}</td>
                  <td>{{fee.FeeType?.Name}}</td>
                  <td>{{StageLabel(fee.FeeType?.Stage)}}</td>
                  <td>{{fee.Active ? 'Yes' : 'No'}}</td>
                  @if (CanWrite) {
                    <td class="has-text-right">
                      <a class="button is-small" [routerLink]="['fees', fee.Id, 'edit']">Edit</a>
                    </td>
                  }
                </tr>
              }
            </tbody>
          </table>
        }
      </div>
    </article>

    <article class="panel">
      <p class="panel-heading">Breakdown Preview</p>
      <div class="panel-block is-block">
        <p class="mb-3">Enter a sample amount to see how the current active fees break it down. Either select a Target Type to preview under that type's actual configured pricing mode, or leave it blank and pick Inclusive/Exclusive directly to sanity-check either calculation on its own (the way untyped callers like the mobile app always calculate).</p>
        <div class="field">
          <label class="label">Target Type (optional)</label>
          <div class="control select is-fullwidth">
            <select [(ngModel)]="PreviewTargetType">
              <option value="">None - use Pricing Mode below</option>
              @for (item of TargetTypes ?? []; track item.Name) {
                <option value="{{item.Name}}">{{item.DisplayName}}</option>
              }
            </select>
          </div>
        </div>
        <div class="field">
          <label class="label">Pricing Mode</label>
          <div class="control select is-fullwidth">
            <select [(ngModel)]="PreviewPricingMode" [disabled]="!!PreviewTargetType">
              <option [ngValue]="0">Inclusive (amount is a total, fees reverse-calculated)</option>
              <option [ngValue]="1">Exclusive (amount is a base, fees added on top)</option>
            </select>
          </div>
        </div>
        <div class="field has-addons">
          <div class="control">
            <input type="number" min="0" step="0.01" class="input" placeholder="Amount" [(ngModel)]="PreviewAmount" />
          </div>
          <div class="control">
            <button class="button is-primary" (click)="RunPreview()">Preview</button>
          </div>
        </div>
        @if (PreviewRequested) {
          @if (Preview == null) {
            <p>Nothing to break down - the organization has no active fees, or the total cannot be unwound from this configuration.</p>
          } @else {
            <table class="table is-fullwidth">
              <tbody>
                <tr>
                  <td>Base Amount</td>
                  <td class="has-text-right">{{Preview.BaseAmount | number:'1.2-2'}}</td>
                </tr>
                @for (line of Preview.Lines; track line.Name) {
                  <tr>
                    <td>{{line.Name}} @if (line.Percentage != null) { ({{line.Percentage}}%) }</td>
                    <td class="has-text-right">{{line.Amount | number:'1.2-2'}}</td>
                  </tr>
                }
                <tr>
                  <th>Total</th>
                  <th class="has-text-right">{{Preview.TotalAmount | number:'1.2-2'}}</th>
                </tr>
              </tbody>
            </table>
          }
        }
      </div>
    </article>
  </div>

  <div class="column is-5">
    <article class="panel">
      <p class="panel-heading">Fee Types</p>
      <div class="panel-block is-block">
        <p class="mb-3">Fee types define the category and calculation behavior of a kind of fee - its pipeline stage, whether it is a percentage or flat amount, and what percentages calculate against.</p>
        @if (FeeTypes == null) {
          <p>Loading...</p>
        } @else if (FeeTypes.length == 0) {
          <p>No fee types configured yet.</p>
        } @else {
          <table class="table is-fullwidth is-striped">
            <thead>
              <tr>
                <th>Name</th>
                <th>Stage</th>
                <th>Value Kind</th>
                <th>Basis</th>
                @if (CanWrite) { <th></th> }
              </tr>
            </thead>
            <tbody>
              @for (feeType of FeeTypes; track feeType.Id) {
                <tr>
                  <td>{{feeType.Name}}</td>
                  <td>{{StageLabel(feeType.Stage)}}</td>
                  <td>{{ValueKindLabel(feeType.ValueKind)}}</td>
                  <td>{{feeType.ValueKind == 1 ? '-' : BasisLabel(feeType.Basis)}}</td>
                  @if (CanWrite) {
                    <td class="has-text-right">
                      <a class="button is-small" [routerLink]="['feetypes', feeType.Id, 'edit']">Edit</a>
                    </td>
                  }
                </tr>
              }
            </tbody>
          </table>
        }
      </div>
    </article>

    <article class="panel">
      <p class="panel-heading">Pricing Modes</p>
      <div class="panel-block is-block">
        <p class="mb-3">Configures whether amounts quoted for a payment entity type already include fees (Inclusive) or fees should be added on top (Exclusive). Types with no pricing mode configured default to Inclusive.</p>
        @if (PricingModes == null) {
          <p>Loading...</p>
        } @else if (PricingModes.length == 0) {
          <p>No pricing modes configured yet - all entity types default to Inclusive.</p>
        } @else {
          <table class="table is-fullwidth is-striped">
            <thead>
              <tr>
                <th>Target Type</th>
                <th>Pricing Mode</th>
                @if (CanWrite) { <th></th> }
              </tr>
            </thead>
            <tbody>
              @for (pricingMode of PricingModes; track pricingMode.Id) {
                <tr>
                  <td>{{TargetTypeLabel(pricingMode.TargetType)}}</td>
                  <td>{{PricingModeLabel(pricingMode.PricingMode)}}</td>
                  @if (CanWrite) {
                    <td class="has-text-right">
                      <a class="button is-small" [routerLink]="['pricingmodes', pricingMode.Id, 'edit']">Edit</a>
                    </td>
                  }
                </tr>
              }
            </tbody>
          </table>
        }
      </div>
    </article>
  </div>
</div>
